import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { getAvailableTemplates, getCustomTemplates, saveCustomTemplate, deleteCustomTemplate } from '../config/exportTemplates';
import { getExportPreviewFrame } from '../utils/clientExport';
import './ExportVideoModal.css';

const PREVIEW_WIDTH = 320;
const PREVIEW_HEIGHT = 180;
const PREVIEW_DEBOUNCE_MS = 350;

const THUMB_HIT_RADIUS = 18;

/**
 * Dual-thumb range slider. Only the thumbs are draggable; track clicks are ignored
 * so the two thumbs never snap together.
 */
function DualRangeSlider({ min, max, step, startValue, endValue, onStartChange, onEndChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(null); // 'start' | 'end' | null
  const [hoverNear, setHoverNear] = React.useState(null); // 'start' | 'end' | null

  const pixelToValue = React.useCallback((pixelX) => {
    const el = trackRef.current;
    if (!el) return min;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (pixelX - rect.left) / rect.width));
    const raw = min + pct * (max - min);
    return Math.round(raw / step) * step;
  }, [min, max, step]);

  React.useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const v = pixelToValue(e.clientX);
      if (dragging === 'start') onStartChange(Math.min(v, Math.max(min, endValue - step)));
      else onEndChange(Math.max(v, Math.min(max, startValue + step)));
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, pixelToValue, onStartChange, onEndChange, min, max, step, startValue, endValue]);

  const handleTrackMouseMove = React.useCallback((e) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const startX = (startValue / max) * rect.width;
    const endX = (endValue / max) * rect.width;
    const distToStart = Math.abs(clickX - startX);
    const distToEnd = Math.abs(clickX - endX);
    if (distToEnd <= THUMB_HIT_RADIUS && distToEnd <= distToStart) {
      setHoverNear('end');
    } else if (distToStart <= THUMB_HIT_RADIUS) {
      setHoverNear('start');
    } else {
      setHoverNear(null);
    }
  }, [startValue, endValue, max]);

  const handleTrackMouseDown = React.useCallback((e) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const startX = (startValue / max) * rect.width;
    const endX = (endValue / max) * rect.width;
    const distToStart = Math.abs(clickX - startX);
    const distToEnd = Math.abs(clickX - endX);
    if (distToEnd <= THUMB_HIT_RADIUS && distToEnd <= distToStart) {
      e.preventDefault();
      setDragging('end');
    } else if (distToStart <= THUMB_HIT_RADIUS) {
      e.preventDefault();
      setDragging('start');
    } else {
      e.preventDefault();
    }
  }, [startValue, endValue, max]);

  return (
    <div
      ref={trackRef}
      className={`export-video-modal-range-dual-track${dragging ? ' export-video-modal-range-dual-track--dragging' : ''}${hoverNear ? ' export-video-modal-range-dual-track--hover' : ''}`}
      onMouseDown={handleTrackMouseDown}
      onMouseMove={handleTrackMouseMove}
      onMouseLeave={() => setHoverNear(null)}
      role="group"
      aria-label="Export time range"
    >
      <div
        className="export-video-modal-range-dual-thumb export-video-modal-range-dual-thumb--start"
        style={{ left: `${(startValue / max) * 100}%` }}
      />
      <div
        className="export-video-modal-range-dual-thumb export-video-modal-range-dual-thumb--end"
        style={{ left: `${(endValue / max) * 100}%` }}
      />
    </div>
  );
}

/** Format seconds as H:MM:SS.cc (hours:minutes:seconds.centiseconds), e.g. 181.5 → "0:03:01.50" */
function formatTimeHMS(seconds) {
  const s = Math.max(0, Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = (sec < 10 ? '0' : '') + sec.toFixed(2);
  return h > 0 ? `${h}:${mm}:${ss}` : `0:${mm}:${ss}`;
}

const defaultBox = () => ({ xPct: 0.76, yPct: 0.03, widthPct: 0.24, heightPct: 0.22 });
const defaultGraphBox = () => ({ xPct: 0, yPct: 0.68, widthPct: 1, heightPct: 0.32, zoom: 1 }); // y from bottom, height 32%; zoom 1 = full timeline

const ExportVideoModal = ({
  isOpen,
  onClose,
  onStartExport,
  hasTelemetry,
  hasGps,
  starting,
  progress,
  message,
  videoUrl,
  telemetryData = [],
  videoDuration: initialVideoDuration,
  chartVisibleMetrics,
  dataOffset = 0,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [exportStartSec, setExportStartSec] = useState(0);
  const [exportEndSec, setExportEndSec] = useState(initialVideoDuration ?? 0);
  const [videoDuration, setVideoDuration] = useState(initialVideoDuration ?? 0);
  const [previewDataUrl, setPreviewDataUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showCustomEditor, setShowCustomEditor] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [customLayout, setCustomLayout] = useState(() => ({
    graph: defaultGraphBox(),
    gps: defaultBox(),
  }));
  const [customShowGraph, setCustomShowGraph] = useState(true);
  const [customShowGps, setCustomShowGps] = useState(true);
  const [layoutEditorTab, setLayoutEditorTab] = useState('graph'); // 'graph' | 'gps'
  const [customTemplateName, setCustomTemplateName] = useState('');
  const [customTemplates, setCustomTemplates] = useState(() => getCustomTemplates());

  const templates = getAvailableTemplates({ hasTelemetry, hasGps });
  const previewDebounceRef = useRef(null);
  const lastPreviewKeyRef = useRef('');
  const lastPreviewDataUrlRef = useRef(null);
  const updatePreviewRef = useRef(null);

  // Resolve video duration when modal opens (if we have videoUrl but no duration)
  // Skip loading YouTube URLs in a video element - CORS blocks direct access
  const isYouTubeVideo = videoUrl && (String(videoUrl).includes('youtube.com') || String(videoUrl).includes('youtu.be'));
  const durationFromTelemetry = useMemo(() => {
    if (!telemetryData?.length) return 0;
    let maxT = 0;
    for (let i = 0; i < telemetryData.length; i++) {
      const t = telemetryData[i]?.time ?? telemetryData[i]?.timestamp ?? telemetryData[i]?.elapsed ?? telemetryData[i]?.Time;
      if (typeof t === 'number' && Number.isFinite(t)) maxT = Math.max(maxT, t);
    }
    return maxT > 0 ? maxT : 0;
  }, [telemetryData]);
  useEffect(() => {
    if (!isOpen || !videoUrl) return;
    if (initialVideoDuration != null && initialVideoDuration > 0) {
      setVideoDuration(initialVideoDuration);
      return;
    }
    if (isYouTubeVideo && durationFromTelemetry > 0) {
      setVideoDuration(durationFromTelemetry);
      setExportEndSec(durationFromTelemetry);
      return;
    }
    if (isYouTubeVideo) {
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = videoUrl;
    video.onloadedmetadata = () => {
      const d = video.duration;
      if (Number.isFinite(d) && d > 0) {
        setVideoDuration(d);
        setExportEndSec(d);
      }
      video.src = '';
    };
    video.onerror = () => {};
    video.load();
    return () => { video.src = ''; };
  }, [isOpen, videoUrl, initialVideoDuration, isYouTubeVideo, durationFromTelemetry]);

  useEffect(() => {
    const d = initialVideoDuration ?? (isYouTubeVideo ? durationFromTelemetry : 0);
    if (d != null && d > 0) {
      setVideoDuration(d);
      if (exportEndSec === 0) setExportEndSec(d);
    }
  }, [initialVideoDuration, isYouTubeVideo, durationFromTelemetry]);

  useEffect(() => {
    if (!isOpen) return;
    const list = getAvailableTemplates({ hasTelemetry, hasGps });
    const firstEnabled = list.find((t) => !t.disabled);
    setSelectedTemplateId(firstEnabled ? firstEnabled.id : null);
    const total = videoDuration || initialVideoDuration || durationFromTelemetry || 0;
    setExportStartSec(0);
    setExportEndSec(isYouTubeVideo && total > 30 ? 30 : total);
    lastPreviewDataUrlRef.current = null;
    setPreviewDataUrl(null);
    setShowCustomEditor(false);
    setEditingTemplateId(null);
    setLayoutEditorTab('graph');
    setCustomTemplates(getCustomTemplates());
  }, [isOpen, hasTelemetry, hasGps, videoDuration, initialVideoDuration, isYouTubeVideo, durationFromTelemetry]);

  // Clamp export range when duration changes
  useEffect(() => {
    if (videoDuration <= 0) return;
    setExportStartSec((s) => Math.min(s, Math.max(0, videoDuration - 1)));
    setExportEndSec((e) => Math.min(Math.max(e, 1), videoDuration));
  }, [videoDuration]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const isCustom = selectedTemplate?.custom === true;
  const layoutConfigForExport = isCustom && selectedTemplate?.layout ? selectedTemplate.layout : null;

  const getPreviewLayoutConfig = useCallback(() => {
    if (showCustomEditor) {
      return {
        graph: customShowGraph ? customLayout.graph : undefined,
        gps: customShowGps ? customLayout.gps : undefined,
      };
    }
    return isCustom && selectedTemplate?.layout ? selectedTemplate.layout : null;
  }, [showCustomEditor, customShowGraph, customShowGps, customLayout, isCustom, selectedTemplate]);

  const updatePreview = useCallback(() => {
    if (!videoUrl) {
      lastPreviewDataUrlRef.current = null;
      setPreviewDataUrl(null);
      return;
    }
    const templateId = showCustomEditor ? (selectedTemplateId || 'video_graph_gps') : selectedTemplateId;
    const template = templates.find((t) => t.id === templateId);
    if (!templateId || (template?.disabled && !showCustomEditor)) {
      lastPreviewDataUrlRef.current = null;
      setPreviewDataUrl(null);
      return;
    }
    const timeSec = Math.max(0, exportStartSec + (exportEndSec - exportStartSec) * 0.5);
    const layoutCfg = getPreviewLayoutConfig();
    const key = [
      videoUrl,
      templateId,
      timeSec.toFixed(1),
      exportStartSec.toFixed(1),
      exportEndSec.toFixed(1),
      showCustomEditor ? JSON.stringify(layoutCfg) : '',
      telemetryData?.length ?? 0,
    ].join('|');
    if (key === lastPreviewKeyRef.current) return;
    lastPreviewKeyRef.current = key;

    if (!lastPreviewDataUrlRef.current) setPreviewLoading(true);
    const opts = {
      videoUrl,
      telemetryData,
      timeSec,
      dataOffset: dataOffset ?? 0,
      width: PREVIEW_WIDTH,
      height: PREVIEW_HEIGHT,
    };
    if (layoutCfg && (layoutCfg.graph || layoutCfg.gps)) {
      opts.layoutConfig = layoutCfg;
      opts.templateId = hasTelemetry && hasGps ? 'video_graph_gps' : hasTelemetry ? 'video_graph_only' : 'video_gps_only';
    } else {
      opts.templateId = templateId;
    }
    opts.visibleMetrics = chartVisibleMetrics ?? {};
    getExportPreviewFrame(opts)
      .then((dataUrl) => {
        lastPreviewDataUrlRef.current = dataUrl;
        setPreviewDataUrl(dataUrl);
      })
      .catch(() => {
        lastPreviewDataUrlRef.current = null;
        setPreviewDataUrl(null);
      })
      .finally(() => setPreviewLoading(false));
  }, [videoUrl, selectedTemplateId, templates, telemetryData, exportStartSec, exportEndSec, showCustomEditor, getPreviewLayoutConfig, hasTelemetry, hasGps, chartVisibleMetrics, dataOffset]);
  updatePreviewRef.current = updatePreview;

  const customLayoutKey = useMemo(() => {
    const g = customLayout?.graph;
    const p = customLayout?.gps;
    return [g?.xPct, g?.yPct, g?.widthPct, g?.heightPct, g?.zoom, p?.xPct, p?.yPct, p?.widthPct, p?.heightPct].join(',');
  }, [customLayout?.graph?.xPct, customLayout?.graph?.yPct, customLayout?.graph?.widthPct, customLayout?.graph?.heightPct, customLayout?.graph?.zoom, customLayout?.gps?.xPct, customLayout?.gps?.yPct, customLayout?.gps?.widthPct, customLayout?.gps?.heightPct]);

  useEffect(() => {
    if (!isOpen) return;
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    const run = () => {
      lastPreviewKeyRef.current = '';
      if (updatePreviewRef.current) updatePreviewRef.current();
    };
    previewDebounceRef.current = setTimeout(run, PREVIEW_DEBOUNCE_MS);
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, [isOpen, selectedTemplateId, exportStartSec, exportEndSec, showCustomEditor, customShowGraph, customShowGps, customLayoutKey, chartVisibleMetrics, dataOffset]);

  const handleStart = () => {
    if (!selectedTemplateId) return;
    const selected = templates.find((t) => t.id === selectedTemplateId);
    if (selected?.disabled) return;
    const options = {
      startTimeSec: exportStartSec,
      endTimeSec: exportEndSec,
      dataOffset,
      visibleMetrics: chartVisibleMetrics ?? {},
    };
    if (selected?.custom && selected?.layout) options.layoutConfig = selected.layout;
    onStartExport(selectedTemplateId, options);
  };

  const normalizeLayoutToEditor = (layout) => {
    if (!layout) return { graph: defaultGraphBox(), gps: defaultBox() };
    const baseGraph = layout.graph ?? (layout.graphHeightPct != null
      ? { xPct: 0, yPct: 1 - layout.graphHeightPct, widthPct: 1, heightPct: layout.graphHeightPct }
      : defaultGraphBox());
    const graph = { ...defaultGraphBox(), ...baseGraph, zoom: baseGraph.zoom ?? 1 };
    const gps = layout.gps ?? defaultBox();
    return { graph, gps };
  };

  const openEditorForNew = () => {
    setCustomLayout({ graph: defaultGraphBox(), gps: defaultBox() });
    setCustomShowGraph(true);
    setCustomShowGps(true);
    setCustomTemplateName('');
    setEditingTemplateId(null);
    setLayoutEditorTab('graph');
    setShowCustomEditor(true);
  };

  const openEditorForTemplate = (tpl) => {
    if (!tpl?.layout) {
      openEditorForNew();
      return;
    }
    const { graph, gps } = normalizeLayoutToEditor(tpl.layout);
    setCustomLayout({ graph, gps });
    setCustomShowGraph(!!(tpl.layout.graph || (tpl.layout.graphHeightPct != null && tpl.layout.graphHeightPct > 0)));
    setCustomShowGps(!!tpl.layout.gps);
    setCustomTemplateName(tpl.label || '');
    setEditingTemplateId(tpl.id);
    setLayoutEditorTab('graph');
    setShowCustomEditor(true);
  };

  const canStart = selectedTemplateId && !templates.find((t) => t.id === selectedTemplateId)?.disabled;
  const hasAnyEnabled = templates.some((t) => !t.disabled);
  const durationSec = videoDuration > 0 ? videoDuration : (initialVideoDuration ?? (isYouTubeVideo ? durationFromTelemetry : 0));

  const handleSaveCustomTemplate = () => {
    const layout = {
      graph: customShowGraph ? customLayout.graph : undefined,
      gps: customShowGps ? customLayout.gps : undefined,
    };
    if (!customShowGraph && !customShowGps) return;
    saveCustomTemplate({
      id: editingTemplateId || undefined,
      label: customTemplateName || 'Custom layout',
      layout,
    });
    setCustomTemplates(getCustomTemplates());
    setShowCustomEditor(false);
    setCustomTemplateName('');
    setEditingTemplateId(null);
    const list = getAvailableTemplates({ hasTelemetry, hasGps });
    const target = editingTemplateId ? list.find((t) => t.id === editingTemplateId) : list.filter((t) => t.custom).pop();
    if (target) setSelectedTemplateId(target.id);
  };

  const handleDeleteCustom = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    deleteCustomTemplate(id);
    setCustomTemplates(getCustomTemplates());
    if (selectedTemplateId === id) {
      const first = getAvailableTemplates({ hasTelemetry, hasGps }).find((t) => !t.disabled);
      setSelectedTemplateId(first ? first.id : null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="export-video-modal-overlay" onClick={onClose}>
      <div className={`export-video-modal ${showCustomEditor ? 'export-video-modal--wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <h2 className="export-video-modal-title">Export video</h2>
        <p className="export-video-modal-description">
          Choose a layout and optional time range. The export runs in the background; you will get a download link when it is ready.
          {isYouTubeVideo && ' For YouTube videos, the export is processed on the server.'}
        </p>
        {message && (
          <p className="export-video-modal-message" role="alert">
            {message}
          </p>
        )}

        {/* Layout template dropdown */}
        <div className="export-video-modal-section">
          <span className="export-video-modal-section-title">Layout template</span>
          <div className="export-video-modal-dropdown-row">
            <select
              className="export-video-modal-select"
              value={selectedTemplateId || ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__new__') {
                  openEditorForNew();
                  return;
                }
                setSelectedTemplateId(v || null);
              }}
              aria-label="Layout template"
            >
              {templates.filter((t) => !t.disabled).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
              <option value="__new__">New layout…</option>
            </select>
            {isCustom && selectedTemplateId && (
              <>
                <button type="button" className="export-video-modal-custom-btn" onClick={() => openEditorForTemplate(selectedTemplate)}>
                  Edit
                </button>
                <button type="button" className="export-video-modal-template-delete" onClick={(e) => handleDeleteCustom(e, selectedTemplateId)} title="Delete template" aria-label="Delete template">
                  ×
                </button>
              </>
            )}
            {!isCustom && (
              <button type="button" className="export-video-modal-custom-btn" onClick={openEditorForNew}>
                New layout…
              </button>
            )}
          </div>
        </div>

        {/* Preview + Layout editor: side by side when editing, or preview alone */}
        <div className={`export-video-modal-section ${showCustomEditor ? 'export-video-modal-preview-editor-row' : ''}`}>
          {showCustomEditor ? (
            <>
              <div className="export-video-modal-preview-cell">
                <span className="export-video-modal-section-title">Preview</span>
                <div className="export-video-modal-preview">
                  {previewLoading && <div className="export-video-modal-preview-loading">Loading…</div>}
                  {!previewLoading && previewDataUrl && <img src={previewDataUrl} alt="Layout preview" className="export-video-modal-preview-img" />}
                  {!previewLoading && !previewDataUrl && selectedTemplateId && <div className="export-video-modal-preview-placeholder">No preview</div>}
                  {!selectedTemplateId && <div className="export-video-modal-preview-placeholder">Select a layout</div>}
                </div>
              </div>
              <div className="export-video-modal-custom-editor">
                <span className="export-video-modal-section-title">{editingTemplateId ? 'Edit layout' : 'New layout'}</span>
                <div className="export-video-modal-editor-tabs">
                  <button
                    type="button"
                    className={`export-video-modal-editor-tab ${layoutEditorTab === 'graph' ? 'export-video-modal-editor-tab--active' : ''}`}
                    onClick={() => setLayoutEditorTab('graph')}
                  >
                    Graph
                  </button>
                  <button
                    type="button"
                    className={`export-video-modal-editor-tab ${layoutEditorTab === 'gps' ? 'export-video-modal-editor-tab--active' : ''}`}
                    onClick={() => setLayoutEditorTab('gps')}
                  >
                    GPS
                  </button>
                </div>
                {layoutEditorTab === 'graph' && (
                  <div className="export-video-modal-editor-tab-panel">
                    <label className="export-video-modal-custom-row">
                      <input type="checkbox" checked={customShowGraph} onChange={(e) => setCustomShowGraph(e.target.checked)} />
                      <span>Show telemetry graph</span>
                    </label>
                    {customShowGraph && (
                      <div className="export-video-modal-box-controls">
                        <span className="export-video-modal-box-label">Graph (x, y, width, height)</span>
                        {['xPct', 'yPct', 'widthPct', 'heightPct'].map((key) => (
                          <label key={key} className="export-video-modal-custom-row">
                            <span>{key.replace('Pct', ' %')}</span>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={Math.round((customLayout.graph?.[key] ?? (key === 'widthPct' || key === 'heightPct' ? 32 : 0)) * 100)}
                              onChange={(e) => setCustomLayout((l) => ({ ...l, graph: { ...(l.graph || defaultGraphBox()), [key]: Number(e.target.value) / 100 } }))}
                            />
                            <span>{Math.round((customLayout.graph?.[key] ?? 0) * 100)}%</span>
                          </label>
                        ))}
                        <label className="export-video-modal-custom-row">
                          <span>Graph zoom</span>
                          <input
                            type="range"
                            min="1"
                            max="15"
                            step="0.5"
                            value={customLayout.graph?.zoom ?? 1}
                            onChange={(e) => setCustomLayout((l) => ({ ...l, graph: { ...(l.graph || defaultGraphBox()), zoom: Math.max(1, Number(e.target.value)) } }))}
                          />
                          <span>{(customLayout.graph?.zoom ?? 1).toFixed(1)}×</span>
                        </label>
                        <p className="export-video-modal-zoom-hint">Zoomed graph scrolls horizontally; current position stays centered.</p>
                      </div>
                    )}
                  </div>
                )}
                {layoutEditorTab === 'gps' && (
                  <div className="export-video-modal-editor-tab-panel">
                    <label className="export-video-modal-custom-row">
                      <input type="checkbox" checked={customShowGps} onChange={(e) => setCustomShowGps(e.target.checked)} />
                      <span>Show GPS map</span>
                    </label>
                    {customShowGps && (
                      <div className="export-video-modal-box-controls">
                        <span className="export-video-modal-box-label">GPS (x, y, width, height)</span>
                        {['xPct', 'yPct', 'widthPct', 'heightPct'].map((key) => (
                          <label key={key} className="export-video-modal-custom-row">
                            <span>{key.replace('Pct', ' %')}</span>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={Math.round((customLayout.gps?.[key] ?? defaultBox()[key]) * 100)}
                              onChange={(e) => setCustomLayout((l) => ({ ...l, gps: { ...(l.gps || defaultBox()), [key]: Number(e.target.value) / 100 } }))}
                            />
                            <span>{Math.round((customLayout.gps?.[key] ?? 0) * 100)}%</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <label className="export-video-modal-custom-row">
                  <span>Template name</span>
                  <input
                    type="text"
                    placeholder="My layout"
                    value={customTemplateName}
                    onChange={(e) => setCustomTemplateName(e.target.value)}
                  />
                </label>
                <div className="export-video-modal-editor-actions">
                  <button type="button" className="export-video-modal-btn export-video-modal-btn--secondary" onClick={() => setShowCustomEditor(false)}>
                    Cancel
                  </button>
                  <button type="button" className="export-video-modal-btn export-video-modal-btn--primary" onClick={handleSaveCustomTemplate} disabled={!customShowGraph && !customShowGps}>
                    {editingTemplateId ? 'Save changes' : 'Save template'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <span className="export-video-modal-section-title">Preview</span>
              <div className="export-video-modal-preview">
                {previewLoading && <div className="export-video-modal-preview-loading">Loading…</div>}
                {!previewLoading && previewDataUrl && <img src={previewDataUrl} alt="Layout preview" className="export-video-modal-preview-img" />}
                {!previewLoading && !previewDataUrl && selectedTemplateId && <div className="export-video-modal-preview-placeholder">No preview</div>}
                {!selectedTemplateId && <div className="export-video-modal-preview-placeholder">Select a layout</div>}
              </div>
            </>
          )}
        </div>

        {/* Time range: dual-thumb slider - only thumbs respond; track clicks do nothing */}
        {durationSec > 0 && (
          <div className="export-video-modal-section">
            <span className="export-video-modal-section-title">Export range</span>
            <div className="export-video-modal-range-dual">
              <span className="export-video-modal-range-dual-label" aria-hidden="true">
                {formatTimeHMS(exportStartSec)}
              </span>
              <DualRangeSlider
                min={0}
                max={durationSec}
                step={0.1}
                startValue={exportStartSec}
                endValue={exportEndSec}
                onStartChange={(v) => setExportStartSec(Math.min(v, Math.max(0, exportEndSec - 0.1)))}
                onEndChange={(v) => setExportEndSec(Math.max(v, Math.min(durationSec, exportStartSec + 0.1)))}
              />
              <span className="export-video-modal-range-dual-label" aria-hidden="true">
                {formatTimeHMS(exportEndSec)}
              </span>
            </div>
            <p className="export-video-modal-range-hint">
              Duration: {formatTimeHMS(Math.max(0, exportEndSec - exportStartSec))}
            </p>
          </div>
        )}

        <div className="export-video-modal-actions">
          <button type="button" className="export-video-modal-btn export-video-modal-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="export-video-modal-btn export-video-modal-btn--primary"
            onClick={handleStart}
            disabled={!canStart || !hasAnyEnabled || starting}
          >
            {starting ? (progress === 'uploading' ? 'Uploading…' : progress === 'starting' ? 'Starting…' : progress === 'recording' ? 'Recording…' : progress === 'loading' ? 'Loading…' : 'Starting…') : 'Start export'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportVideoModal;
